import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { ChevronDown, ChevronsUp, History as HistoryIcon, X } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { HistoryEmptyState } from '@/types/history'

/**
 * THE SHAPE BOTH HISTORY LISTS SHARE (History's Changes tab and business Activity): one card per
 * day, a keyset pager, a skeleton while the next page is on its way, and the server's empty state.
 * One implementation, so the two lists read the same (EP-36).
 *
 * The server bounds every list to one page (50 items, R2.12) and builds both pager links, filters
 * and cursor included. The pager renders those hrefs as they are: it appends nothing.
 */

/** One day of a history list: the server's day label, then its items. */
export function DayCard({
  label,
  date,
  children,
  delay = 0,
}: {
  label: string
  date: string
  children: React.ReactNode
  delay?: number
}) {
  return (
    <section
      className="animate-slide-in overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm"
      style={{ animationDelay: `${delay}ms` }}
      aria-label={label}
      data-day={date}
    >
      <header className="border-b border-border/40 px-5 py-4">
        <h2 className="text-sm font-semibold text-foreground">
          <time dateTime={date}>{label}</time>
        </h2>
      </header>
      <ul className="divide-y divide-border/40">{children}</ul>
    </section>
  )
}

/** The stagger for the day cards: the first few step in, the rest arrive with the last of them. */
export function dayDelay(index: number, start: number): number {
  return start + Math.min(index, 4) * 50
}

type VisitCallbacks = { onStart: () => void; onFinish: () => void }

/**
 * Whether a visit this page started (a filter change, a page of older items) is still on its way.
 * Only visits that pass `callbacks` count, so a background reload elsewhere on the page never
 * blanks the list. It reports pending only after a short wait, so a quick answer never flashes a
 * skeleton.
 */
export function usePendingVisit(delayMs = 150): { pending: boolean; callbacks: VisitCallbacks } {
  const [pending, setPending] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onStart = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setPending(true), delayMs)
  }, [delayMs])

  const onFinish = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    setPending(false)
  }, [])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  return { pending, callbacks: { onStart, onFinish } }
}

/** A day card's shape while the list is on its way. A skeleton, never a spinner (X7). */
export function TimelineSkeleton({ rows = 4 }: { rows?: number }) {
  const bar = 'motion-reduce:animate-none'
  return (
    <div
      className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm"
      aria-hidden="true"
      data-testid="timeline-skeleton"
    >
      <div className="border-b border-border/40 px-5 py-4">
        <Skeleton className={cn('h-4 w-28', bar)} />
      </div>
      <div className="divide-y divide-border/40">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-start gap-3 px-5 py-4">
            <Skeleton className={cn('hidden h-3 w-9 sm:block', bar)} />
            <Skeleton className={cn('h-8 w-8 shrink-0 rounded-full', bar)} />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className={cn('h-3.5 w-48 max-w-full', bar)} />
              <Skeleton className={cn('h-3.5', i % 2 === 0 ? 'w-[85%]' : 'w-[65%]', bar)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** The server's empty state, in the design system's empty-state card. */
export function TimelineEmpty({
  emptyState,
  onClear,
  icon: Icon = HistoryIcon,
}: {
  emptyState: HistoryEmptyState
  /** Given when filters are applied: offers "Clear filters". */
  onClear?: () => void
  icon?: React.ElementType
}) {
  return (
    <div
      className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm"
      style={{ animationDelay: '100ms' }}
      data-testid="timeline-empty"
    >
      <Icon className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">{emptyState.title}</p>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">{emptyState.body}</p>
      {onClear && (
        <Button variant="outline" size="sm" className="mt-4 rounded-lg" onClick={onClear}>
          <X className="mr-1.5 h-3.5 w-3.5" />
          Clear filters
        </Button>
      )}
    </div>
  )
}

/**
 * "Back to newest" while an older page is open, "Show older …" while there is more. Both are
 * `<Link>`s to server-built hrefs. Older keeps the scroll position, then brings the top of the list
 * into view, so the reader lands on the first older item rather than on the page heading or at the
 * foot of the new page.
 */
export function TimelinePager({
  nextHref,
  newestHref,
  olderLabel,
  callbacks,
  listTop,
}: {
  nextHref: string | null
  newestHref: string | null
  olderLabel: string
  callbacks: VisitCallbacks
  listTop?: React.RefObject<HTMLElement | null>
}) {
  if (!nextHref && !newestHref) return null

  const linkClass = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'rounded-lg')
  return (
    <nav aria-label="Pages" className="flex flex-wrap items-center justify-between gap-3">
      {newestHref ? (
        <Link href={newestHref} className={linkClass} onStart={callbacks.onStart} onFinish={callbacks.onFinish}>
          <ChevronsUp className="mr-1.5 h-3.5 w-3.5" />
          Back to newest
        </Link>
      ) : (
        <span />
      )}
      {nextHref && (
        <Link
          href={nextHref}
          preserveScroll
          className={linkClass}
          onStart={callbacks.onStart}
          onFinish={callbacks.onFinish}
          onSuccess={() => listTop?.current?.scrollIntoView({ block: 'start' })}
        >
          {olderLabel}
          <ChevronDown className="ml-1.5 h-3.5 w-3.5" />
        </Link>
      )}
    </nav>
  )
}
